import {
  Chip,
  styled,
  Table,
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@zeiss/beyond-online-react';
import { useBreakpoint } from '@zeiss/lib-helpers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  InvitationDesktopRow,
  InvitationMobileCard,
} from './InvitationsTableItems';
import { NOT_AVAILABLE } from 'src/constants';
import { SendInvitationModalData } from 'src/modules/Invitations/components/SendInvitationModal';
import { getInvitationDetailPath } from 'src/modules/Invitations/utils';
import {
  GetInvitationResponse,
  GetInvitationsResponse,
} from 'src/types/rest/managementPortalApi';

interface InvitationsTableProps {
  data: GetInvitationsResponse['invitations'];
  onSendInvitation: (payload: SendInvitationModalData) => void;
}

export type GetInvitedToProps = Pick<
  GetInvitationResponse,
  'applicationName' | 'tenantName'
>;

export type OnViewInvitationProps = Pick<
  GetInvitationResponse,
  'applicationId' | 'code' | 'tenantId' | 'type'
>;

const FlexContainer = styled.div`
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  justify-content: flex-start;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  padding-block: 1rem 1.5rem;
  padding-inline: 0 1rem;
`;

const PreviewCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const InvitationsTable = ({
  data,
  onSendInvitation,
}: InvitationsTableProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const isMinTablet = useBreakpoint('tablet');

  const getInvitedTo = ({ applicationName, tenantName }: GetInvitedToProps) => {
    if (applicationName) {
      return (
        <FlexContainer>
          {applicationName}
          <Chip
            color="gray"
            icon="NavigationApps"
            label={t('common:glossary.application')}
            size="s"
          />
        </FlexContainer>
      );
    }

    if (tenantName) {
      return (
        <FlexContainer>
          {tenantName}
          <Chip
            color="gray"
            icon="Store"
            label={t('common:glossary.tenant')}
            size="s"
          />
        </FlexContainer>
      );
    }

    return <FlexContainer>{NOT_AVAILABLE}</FlexContainer>;
  };

  const onViewInvitation = ({
    applicationId,
    code,
    tenantId,
    type,
  }: OnViewInvitationProps) => {
    const searchParam =
      type === 'application' ? { applicationId } : { tenantId };

    const path = getInvitationDetailPath({
      code,
      ...searchParam,
    });

    navigate(path);
  };

  if (isMinTablet) {
    return (
      <Table borderless borderStyle="collapse" hover tableLayout="fixed">
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell>
              {t('common:glossary.inviteCode')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:glossary.email')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:table.header.invitedTo')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:table.header.invitedBy')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:table.header.status')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:table.header.lastUpdated')}
            </StyledTableHeaderCell>
            <StyledTableHeaderCell>
              {t('common:table.header.action_other')}
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((invitation) => (
            <InvitationDesktopRow
              getInvitedTo={getInvitedTo}
              invitation={invitation}
              key={invitation.code}
              onSendInvitation={onSendInvitation}
              onViewInvitation={onViewInvitation}
            />
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <PreviewCardContainer>
      {data.map((invitation) => (
        <InvitationMobileCard
          invitation={invitation}
          key={invitation.code}
          onSendInvitation={onSendInvitation}
          onViewInvitation={onViewInvitation}
        />
      ))}
    </PreviewCardContainer>
  );
};
